/**
 * Predictive observability surfaces for the pool admin (IL-137).
 *
 * Three views, all read-only and refreshed by the parent page on its
 * normal cadence:
 *
 *   1. **Forecast vs. actual** — recent prediction records as a small
 *      inline SVG line chart. Forecast in accent color, actual in gray.
 *      Newest minute on the right; X-axis is real time.
 *
 *   2. **Bin-fill heatmap** — the 7×96 weekly grid (or 1×96 diurnal),
 *      colored by per-bin nEff. White = no data, darker = more samples.
 *      Lets the operator see at a glance whether the histogram has
 *      meaningfully filled in across the week.
 *
 *   3. **Quarantine list** — recent rejections from the AnomalyGate.
 *      Surfaces "why didn't we learn from last Tuesday."
 *
 * No external chart library — inline SVG for the chart, CSS Grid for
 * the heatmap. Console doesn't ship recharts/d3 today and these surfaces
 * don't justify adding ~80 KB of dependencies.
 */

import React, { useMemo } from "react";

interface PredictionRow {
  timestampMs: number;
  forecastValue: number;
  contributed: number;
  actualDemand: number;
  coldStartStage: number;
  nEff: number;
  mode: string;
  region: string | null;
  reason: string | null;
}

interface BinRow {
  tBin: number;
  nEff: number;
  weeksObserved: number;
  lastUpdatedAt: number;
}

interface QuarantineRow {
  id: number;
  tBin: number;
  observation: number;
  reason: string;
  atMs: number;
}

interface Props {
  predictions: PredictionRow[];
  bins: BinRow[];
  quarantine: QuarantineRow[];
  tz: string;
}

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const QH_PER_DAY = 96;
const SECTION_HEADING_STYLE: React.CSSProperties = {
  margin: 0,
  fontSize: "var(--text-xs)",
  fontWeight: "var(--weight-semibold)",
  textTransform: "uppercase",
  letterSpacing: "var(--tracking-wide)",
  color: "var(--text-secondary)",
  marginBottom: "var(--space-2)",
};

export function PredictiveObservability({ predictions, bins, quarantine, tz }: Props) {
  const sortedPredictions = useMemo(
    () => [...predictions].sort((a, b) => a.timestampMs - b.timestampMs),
    [predictions],
  );

  return (
    <div style={{ marginTop: "var(--space-4)", paddingTop: "var(--space-3)", borderTop: "var(--border-width) dashed var(--border)", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <ForecastVsActualChart predictions={sortedPredictions} />
      <BinFillHeatmap bins={bins} tz={tz} />
      <QuarantineList rows={quarantine} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Forecast vs. actual — inline SVG sparkline
// ---------------------------------------------------------------------------

function ForecastVsActualChart({ predictions }: { predictions: PredictionRow[] }) {
  if (predictions.length === 0) {
    return (
      <div>
        <h5 style={SECTION_HEADING_STYLE}>Forecast vs. actual</h5>
        <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          No predictions logged yet. Histograms in stage 0 (cold-start) emit floor=0
          while the model accumulates ≥ 4 weeks of per-bin data.
        </div>
      </div>
    );
  }
  const W = 600;
  const H = 80;
  const PADDING = 6;
  const minTs = predictions[0]!.timestampMs;
  const maxTs = predictions[predictions.length - 1]!.timestampMs;
  const span = Math.max(1, maxTs - minTs);
  let yMax = 1;
  for (const p of predictions) {
    if (p.forecastValue > yMax) yMax = p.forecastValue;
    if (p.actualDemand > yMax) yMax = p.actualDemand;
  }
  yMax *= 1.15; // headroom

  const xOf = (ts: number) => PADDING + ((ts - minTs) / span) * (W - PADDING * 2);
  const yOf = (v: number) => H - PADDING - (v / yMax) * (H - PADDING * 2);

  const forecastPath = predictions
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.timestampMs).toFixed(1)} ${yOf(p.forecastValue).toFixed(1)}`)
    .join(" ");
  const actualPath = predictions
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.timestampMs).toFixed(1)} ${yOf(p.actualDemand).toFixed(1)}`)
    .join(" ");

  const totalForecasts = predictions.length;
  const shadowCount = predictions.filter((p) => p.mode === "shadow").length;
  const stage2Count = predictions.filter((p) => p.coldStartStage === 2).length;
  const capCount = predictions.filter((p) => p.contributed < p.forecastValue && p.mode === "predictive").length;

  // Coverage = fraction of records where actual ≤ forecast (P_x sized correctly)
  const coverageRecords = predictions.filter((p) => p.coldStartStage > 0);
  const covered = coverageRecords.filter((p) => p.actualDemand <= p.forecastValue).length;
  const coveragePct = coverageRecords.length > 0
    ? (100 * covered) / coverageRecords.length
    : null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
        <h5 style={{ ...SECTION_HEADING_STYLE, marginBottom: 0 }}>Forecast vs. actual</h5>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          {totalForecasts} records · {stage2Count} mature · {shadowCount} shadow
          {capCount > 0 ? ` · ${capCount} capped` : ""}
          {coveragePct !== null ? ` · coverage ${coveragePct.toFixed(0)}%` : ""}
        </span>
      </div>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", maxWidth: "100%", border: "var(--border-width) solid var(--border)" }}>
        <path d={actualPath} stroke="var(--gray-6)" strokeWidth={1.2} fill="none" opacity={0.85} />
        <path d={forecastPath} stroke="var(--accent, #c4513d)" strokeWidth={1.5} fill="none" />
      </svg>
      <div style={{ display: "flex", gap: "var(--space-3)", fontSize: "var(--text-xs)", color: "var(--text-secondary)", marginTop: "var(--space-1)" }}>
        <span><span style={{ color: "var(--accent, #c4513d)" }}>━</span> forecast</span>
        <span><span style={{ color: "var(--gray-6)" }}>━</span> actual</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bin-fill heatmap — 7×96 CSS Grid
// ---------------------------------------------------------------------------

function BinFillHeatmap({ bins, tz }: { bins: BinRow[]; tz: string }) {
  // Auto-detect mode by bin count: 672 = weekly, 96 = diurnal.
  const mode: "weekly" | "diurnal" = bins.length <= QH_PER_DAY ? "diurnal" : "weekly";
  const rows = mode === "weekly" ? 7 : 1;
  const cols = QH_PER_DAY;

  const byKey = useMemo(() => {
    const m = new Map<number, BinRow>();
    for (const b of bins) m.set(b.tBin, b);
    return m;
  }, [bins]);

  let maxNEff = 0;
  for (const b of bins) if (b.nEff > maxNEff) maxNEff = b.nEff;

  const cells: React.ReactNode[] = [];
  for (let dow = 0; dow < rows; dow++) {
    for (let qh = 0; qh < cols; qh++) {
      const tBin = mode === "weekly" ? dow * QH_PER_DAY + qh : qh;
      const bin = byKey.get(tBin);
      const intensity = bin && maxNEff > 0 ? Math.min(1, bin.nEff / maxNEff) : 0;
      const bg = bin
        ? `rgba(196, 81, 61, ${0.1 + intensity * 0.7})`
        : "transparent";
      const tooltip = bin
        ? `${mode === "weekly" ? DAYS_OF_WEEK[dow] : "qh"} ${formatQh(qh)} — nEff ${bin.nEff.toFixed(1)}, ${bin.weeksObserved} week(s) observed`
        : `${mode === "weekly" ? DAYS_OF_WEEK[dow] : "qh"} ${formatQh(qh)} — empty`;
      cells.push(
        <div
          key={tBin}
          title={tooltip}
          style={{
            backgroundColor: bg,
            border: "0.5px solid var(--border)",
            height: 14,
          }}
        />,
      );
    }
  }

  const filled = bins.length;
  const total = mode === "weekly" ? 672 : 96;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
        <h5 style={{ ...SECTION_HEADING_STYLE, marginBottom: 0 }}>
          Bin fill ({mode}, TZ {tz})
        </h5>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          {filled}/{total} bins ({((100 * filled) / total).toFixed(0)}%)
          {maxNEff > 0 ? ` · max nEff ${maxNEff.toFixed(1)}` : ""}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `40px repeat(${cols}, 1fr)`, gap: 1, maxWidth: "100%" }}>
        {/* qh-of-day axis labels (every 4 columns = every hour) */}
        <div />
        {Array.from({ length: cols }).map((_, qh) => (
          <div key={`hdr-${qh}`} style={{ fontSize: 8, color: "var(--text-secondary)", textAlign: "center", height: 12 }}>
            {qh % 4 === 0 ? `${qh / 4}` : ""}
          </div>
        ))}
        {/* Rows */}
        {Array.from({ length: rows }).map((_, dow) => (
          <React.Fragment key={`row-${dow}`}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", paddingRight: "var(--space-1)", height: 14, display: "flex", alignItems: "center" }}>
              {mode === "weekly" ? DAYS_OF_WEEK[dow] : "qh"}
            </div>
            {Array.from({ length: cols }).map((_, qh) => {
              const idx = dow * cols + qh;
              return cells[idx];
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function formatQh(qh: number): string {
  const h = Math.floor(qh / 4);
  const m = (qh % 4) * 15;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Quarantine list — simple table
// ---------------------------------------------------------------------------

function QuarantineList({ rows }: { rows: QuarantineRow[] }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--space-2)", marginBottom: "var(--space-2)" }}>
        <h5 style={{ ...SECTION_HEADING_STYLE, marginBottom: 0 }}>Quarantine</h5>
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          {rows.length === 0 ? "no rejections" : `${rows.length} recent rejection(s)`}
        </span>
      </div>
      {rows.length === 0 ? (
        <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          The AnomalyGate hasn't rejected any observations in the recent window.
          Rejections appear here when an observation is an outlier (&gt; 4·IQR from
          P50), pool is unhealthy, dampen is active, or it's a holiday.
        </div>
      ) : (
        <div style={{ maxHeight: 200, overflowY: "auto", border: "var(--border-width) solid var(--border)" }}>
          <table style={{ width: "100%", fontSize: "var(--text-xs)", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--bg-primary)" }}>
              <tr style={{ textAlign: "left" }}>
                <th style={cellStyle}>When</th>
                <th style={cellStyle}>Bin</th>
                <th style={cellStyle}>Observation</th>
                <th style={cellStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: "var(--border-width) solid var(--border)" }}>
                  <td style={cellStyle}>{new Date(r.atMs).toLocaleString()}</td>
                  <td style={cellStyle}>{r.tBin}</td>
                  <td style={cellStyle}>{r.observation.toFixed(1)}</td>
                  <td style={cellStyle}>{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: "var(--space-1) var(--space-2)",
  textAlign: "left",
  fontWeight: "normal",
};
